import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import {
  DialogShellComponent,
  FilledButtonComponent,
  OutlinedButtonComponent,
  PartnerSelectComponent,
  TextFieldComponent,
} from '@custom-components';
import { XEMEX_OID } from '@core/constants';
import { getUserActivePartner } from '@core/models';
import { UserStore } from '@root/app/store/user.store';

/** Result returned when the user confirms; `null`/undefined when cancelled. */
export interface CreateInstallationGroupResult {
  partnerOid: string;
  name: string;
  description?: string;
}

@Component({
  selector: 'xemex-create-installation-group-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    TranslateModule,
    DialogShellComponent,
    FilledButtonComponent,
    OutlinedButtonComponent,
    TextFieldComponent,
    PartnerSelectComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './create-installation-group-dialog.component.html',
})
export class CreateInstallationGroupDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<CreateInstallationGroupDialogComponent>);
  private readonly userStore = inject(UserStore);

  readonly form = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: Validators.required }),
    description: new FormControl('', { nonNullable: true }),
  });

  /** Owner partner picked by a Xemex admin; only used when {@link requiresPartnerSelection}. */
  readonly partnerControl = new FormControl<string | null>(null);
  readonly selectedPartnerOid = signal<string | null>(null);
  /** No partner could be resolved to own the group — shown inline on the field. */
  readonly partnerError = signal(false);

  /** True when no partner can be derived and the user must pick one (e.g. a Xemex admin). */
  readonly requiresPartnerSelection = computed(() => !this.autoPartnerOid());

  constructor() {
    this.partnerControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(partnerOid => {
        this.selectedPartnerOid.set(partnerOid || null);
        this.partnerError.set(false);
      });
  }

  /**
   * Partner derived from the logged-in user's active partner. Xemex admins carry the all-zero
   * placeholder OID ({@link XEMEX_OID}) instead of a real partner, so it's normalized to
   * `undefined` — they have no derivable partner and must pick one explicitly.
   */
  private autoPartnerOid(): string | undefined {
    const partnerOid = getUserActivePartner(this.userStore.user())?.partnerOid;
    return partnerOid && partnerOid !== XEMEX_OID ? partnerOid : undefined;
  }

  private resolvePartnerOid(): string | undefined {
    return this.selectedPartnerOid() ?? this.autoPartnerOid() ?? undefined;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onCreate(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const partnerOid = this.resolvePartnerOid();
    if (!partnerOid) {
      this.partnerError.set(true);
      return;
    }
    const { name, description } = this.form.getRawValue();
    this.dialogRef.close({
      partnerOid,
      name: name.trim(),
      description: description.trim() || undefined,
    } satisfies CreateInstallationGroupResult);
  }
}
